import { normalizeWhitespace } from "@/utils";
import { labelTokenValue, parseLabelToken, type LabelToken } from "./labelFilters";

/**
 * 라벨 카탈로그의 조회 축. URL 이 소유한다(rules.md §17). `?search=` 텍스트와 `?label=key:value`
 * (반복 키) 라벨 조건이다. 둘 다 소스가 클라이언트에서 거른다(`LabelTableSource`).
 */
export type LabelPageFilterState = {
  search: string;
  tokens: readonly LabelToken[];
};

export const EMPTY_LABEL_PAGE_FILTERS: LabelPageFilterState = { search: "", tokens: [] };

const PARAM = { search: "search", label: "label" } as const;

export function readLabelPageFilters(params: URLSearchParams): LabelPageFilterState {
  const seen = new Set<string>();
  const tokens: LabelToken[] = [];
  for (const raw of params.getAll(PARAM.label)) {
    const token = parseLabelToken(raw);
    // 형식 오류와 중복은 알림 없이 버린다. 오래된 딥링크가 빈 목록을 만들지 않게 한다.
    if (token === null || seen.has(labelTokenValue(token))) {
      continue;
    }
    seen.add(labelTokenValue(token));
    tokens.push(token);
  }
  return { search: normalizeWhitespace(params.get(PARAM.search) ?? ""), tokens };
}

// 자기 키만 지우고 다시 쓴다. 입력 객체는 변경하지 않는다.
export function writeLabelPageFilters(
  params: URLSearchParams,
  state: LabelPageFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(PARAM.search);
  next.delete(PARAM.label);
  const search = normalizeWhitespace(state.search);
  if (search !== "") {
    next.set(PARAM.search, search);
  }
  for (const token of state.tokens) {
    next.append(PARAM.label, labelTokenValue(token));
  }
  return next;
}

export function addLabelPageToken(
  state: LabelPageFilterState,
  token: LabelToken,
): LabelPageFilterState {
  const value = labelTokenValue(token);
  if (state.tokens.some((candidate) => labelTokenValue(candidate) === value)) {
    return state;
  }
  return { ...state, tokens: [...state.tokens, token] };
}

export function removeLabelPageToken(
  state: LabelPageFilterState,
  tokenValue: string,
): LabelPageFilterState {
  return {
    ...state,
    tokens: state.tokens.filter((candidate) => labelTokenValue(candidate) !== tokenValue),
  };
}

export function hasLabelPageFilters(state: LabelPageFilterState): boolean {
  return state.search !== "" || state.tokens.length > 0;
}
